/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage} from 'react-intl'

// Components
import {Drawer, DrawerOverlay, DrawerContent, Alert, useBreakpointValue} from '@chakra-ui/react'

// Others
import LoadingSpinner from '../loading-spinner'

import {API_ERROR_MESSAGE} from '../../constants'
import useAsync from '../../commerce-api/hooks/useAsync'
import {useCommerceAPI} from '../../commerce-api/contexts'
import DrawerMenuMobile from './drawer-menu-mobile'
import DrawerMenuDesktop from './drawer-menu-desktop'

/**
 * Convert the navigation items from the API:
 * add the key as id to each item on lvl0 if it's not already there
 *
 * @param {Object} items
 * @return {Array}
 */
const convertNavigationItems = (items) => {
    const itemsList = Object.entries(items || {}).map(([key, value]) => ({
        id: key,
        ...value
    }))

    if (!itemsList.length) {
        return []
    }

    return itemsList
}

/**
 * Hook to fetch the navigation items from the API.
 * @param {boolean} isOpen
 * @return {{loading: boolean, error: null|Error, items: null|Array}}
 */
const useNavigationItems = ({isOpen}) => {
    const api = useCommerceAPI()

    // flag to trigger the fetching of the navigation items
    const [isNavigationItemsNeeded, setIsNavigationItemsNeeded] = useState(isOpen)

    // when the drawer is opened, set the flag to fetch the navigation items
    useEffect(() => {
        if (isOpen) {
            setIsNavigationItemsNeeded(true)
        }
    }, [isOpen])

    const {
        loading,
        data: items,
        error,
        execute: fetchNavigation
    } = useAsync(async () => {
        const items = await api.customAPIs.navigation({})
        return convertNavigationItems(items)
    })

    // fetch the navigation items once it is needed
    useEffect(() => {
        if (isNavigationItemsNeeded) {
            fetchNavigation()
        }
    }, [isNavigationItemsNeeded, fetchNavigation])

    // report loading errors
    useEffect(() => {
        if (error && !loading) {
            console.error('Error fetching navigation items', error)
            // reset the flag, so next time the drawer is opened, we try to fetch the items again
            setIsNavigationItemsNeeded(false)
        }
    }, [error, loading])

    return {loading, error, items}
}

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({
    selected: selectedLvl0Id,
    setSelected: setSelectedLvl0Id,
    isOpen,
    onClose,
    onMyAccountClick
}) => {
    const {loading, error, items} = useNavigationItems({isOpen})
    const isMobile = useBreakpointValue({base: true, lg: false})

    // find the selected lvl0 item that corresponds to the selectedLvl0Id
    const selectedLvl0 = useMemo(() => {
        if (selectedLvl0Id && items) {
            for (const item of items) {
                if (item.id === selectedLvl0Id) {
                    return item
                }
            }
        }
    }, [selectedLvl0Id, items])

    // keep track of selected lvl1 item
    const [selectedLvl1, setSelectedLvl1] = useState(null)

    // clear selected lvl1 item when the selected lvl0 item changes and lvl1 is not from lvl0
    useEffect(() => {
        setSelectedLvl1((current) => (selectedLvl0?.items?.includes(current) ? current : null))
    }, [selectedLvl0])

    // helper for setting the selected item from lvl0
    const setSelectedLvl0 = useCallback(
        (item) => {
            setSelectedLvl0Id?.(item?.id)
            setSelectedLvl1(null)
        },
        [setSelectedLvl0Id]
    )

    // reset the selected item when the drawer is closed
    useEffect(() => {
        if (!isOpen) {
            setSelectedLvl1(null)
        }
    }, [isOpen])

    // skip rendering the drawer until we have items or either error/loading state to report
    if (!items && !error && !loading) {
        return null
    }

    return (
        <Drawer
            isOpen={isOpen}
            onClose={onClose}
            placement="left"
            size="navigation"
            variant="navigation"
            isFullHeight={false}
            scrollBehavior="inside"
        >
            <DrawerOverlay>
                <DrawerContent>
                    {loading ? (
                        <LoadingSpinner />
                    ) : error ? (
                        <Alert status="error" variant="solid">
                            <FormattedMessage {...API_ERROR_MESSAGE} />
                        </Alert>
                    ) : !items ? null : isMobile ? (
                        <DrawerMenuMobile
                            items={items}
                            selectedLvl0={selectedLvl0}
                            selectedLvl1={selectedLvl1}
                            setSelectedLvl0={setSelectedLvl0}
                            setSelectedLvl1={setSelectedLvl1}
                            onMyAccountClick={onMyAccountClick}
                            onClose={onClose}
                        />
                    ) : (
                        <DrawerMenuDesktop
                            items={items}
                            selectedLvl0={selectedLvl0}
                            selectedLvl1={selectedLvl1}
                            setSelectedLvl0={setSelectedLvl0}
                            setSelectedLvl1={setSelectedLvl1}
                        />
                    )}
                </DrawerContent>
            </DrawerOverlay>
        </Drawer>
    )
}

DrawerMenu.propTypes = {
    /**
     * The currently selected item from header and first level of items
     */
    selected: PropTypes.string,
    /**
     * Function called when an item is selected.
     */
    setSelected: PropTypes.func,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Function called when the login button is clicked.
     */
    onMyAccountClick: PropTypes.func
}

export default DrawerMenu
